import { Action } from '@remix-run/router'

type LocationParts = {
  pathname: string
  search: string
  hash: string
}

type HistoryAdapter = ReturnType<
  typeof import('./historyAdapter').createHistoryAdapter
>

type PathNormalizer = ReturnType<
  typeof import('./pathNormalizer').createPathNormalizer
>

type RouterState = {
  location: {
    key: string
    pathname: string
    search: string
    hash: string
  }
  historyAction: Action
}

export const createHistorySync = ({
  historyAdapter,
  pathNormalizer,
  historyIndexKey,
}: {
  historyAdapter: HistoryAdapter
  pathNormalizer: PathNormalizer
  historyIndexKey: string
}) => {
  const history = {
    index: 0,
    keys: [] as string[],
    paths: [] as string[],
  }
  let pendingPopState = { index: -1, path: '' }

  const updateEntry = (
    nextIndex: number,
    nextKey: string,
    nextPath: string,
  ) => {
    history.index = nextIndex
    history.keys[history.index] = nextKey
    history.paths[history.index] = nextPath
  }

  const resetHistory = (nextKey: string, nextPath: string) => {
    history.keys.splice(0, history.keys.length, nextKey)
    history.paths.splice(0, history.paths.length, nextPath)
    history.index = 0
  }

  const replaceBrowserPath = (nextPath: string) => {
    historyAdapter.replaceState(
      {
        ...historyAdapter.getState(),
        [historyIndexKey]: history.index,
      },
      nextPath,
    )
  }

  const pushBrowserPath = (nextPath: string) => {
    historyAdapter.pushState(
      {
        ...historyAdapter.getState(),
        [historyIndexKey]: history.index,
      },
      nextPath,
    )
  }

  const handlePopState = (event: PopStateEvent) => {
    const state =
      event.state && typeof event.state === 'object'
        ? (event.state as Record<string, unknown>)
        : null
    const index =
      typeof state?.[historyIndexKey] === 'number' ? state[historyIndexKey] : -1
    pendingPopState = {
      index,
      path: pathNormalizer.toBrowserPath(historyAdapter.getLocation()),
    }
  }

  const init = (location: LocationParts, key: string) => {
    history.index = 0
    history.keys = [key]
    history.paths = [pathNormalizer.toBrowserPath(location)]
    historyAdapter.replaceState(
      {
        ...historyAdapter.getState(),
        [historyIndexKey]: history.index,
      },
      pathNormalizer.toBrowserPath(location),
    )
  }

  const handleStateChange = (state: RouterState) => {
    const nextPath = pathNormalizer.toBrowserPath(state.location)
    const currentPath = pathNormalizer.toBrowserPath(
      historyAdapter.getLocation(),
    )

    if (state.historyAction === Action.Pop) {
      const keyIndex = history.keys.indexOf(state.location.key)
      const nextIndex =
        keyIndex !== -1 ? keyIndex : history.paths.lastIndexOf(nextPath)
      if (nextIndex === -1) {
        resetHistory(state.location.key, nextPath)
        if (nextPath !== currentPath) {
          replaceBrowserPath(nextPath)
        }
        return
      }

      const delta = nextIndex - history.index
      updateEntry(nextIndex, state.location.key, nextPath)
      if (nextPath !== currentPath && delta !== 0) {
        historyAdapter.go(delta)
      } else if (nextPath === currentPath && delta === 0 && history.index > 0) {
        historyAdapter.go(-1)
      }
      return
    }

    if (state.historyAction === Action.Replace) {
      const popStateIndex = pendingPopState.index
      const popStatePath = pendingPopState.path
      pendingPopState = { index: -1, path: '' }

      if (popStateIndex >= 0) {
        updateEntry(popStateIndex, state.location.key, nextPath)
      } else if (popStatePath) {
        const matchedIndex = history.paths.lastIndexOf(popStatePath)
        if (matchedIndex === -1) {
          resetHistory(state.location.key, nextPath)
          if (nextPath !== currentPath) {
            replaceBrowserPath(nextPath)
          }
          return
        }
        updateEntry(matchedIndex, state.location.key, nextPath)
      } else {
        updateEntry(history.index, state.location.key, nextPath)
      }

      if (nextPath === currentPath) return
      replaceBrowserPath(nextPath)
      return
    }

    const nextIndex = history.index + 1
    history.keys.splice(nextIndex)
    history.paths.splice(nextIndex)
    updateEntry(nextIndex, state.location.key, nextPath)
    if (nextPath === currentPath) return
    pushBrowserPath(nextPath)
  }

  return {
    history,
    init,
    handlePopState,
    handleStateChange,
  }
}
